import { APIUtils } from 'commons/utils';
import axios from 'axios';
import { useQuery } from 'react-query';
import { useRecoilState } from 'recoil';
import { tokenState } from 'redux-recoil-sync.state';
import BlockListResponse from 'containers/composer/blocks/BlockList';
import { Block, Composition } from '@visikon/core-models/content';
import { URLS } from './urls';
import { QueryConfig } from './react-query-utils';
import { BlockSearchRequest } from 'containers/composer/blocks/blockSearch';

export const BLOCK_CACHE_KEY = 'blocks';

export function useBlock(id: string) {
  const [token] = useRecoilState(tokenState);

  return useQuery<Block>(
    [BLOCK_CACHE_KEY, 'single', id],
    async () => {
      const result = await axios.get<Block>(URLS.blocks.get(id!), APIUtils.tokenHeader(token!));

      return result.data;
    },
    { ...QueryConfig, enabled: !!token && id !== 'new' },
  );
}

type BlockUsageReponse = {
  count: number;
  compositions: Composition[];
};

export function useBlockUsage(id: string) {
  const [token] = useRecoilState(tokenState);

  return useQuery<BlockUsageReponse>(
    [BLOCK_CACHE_KEY, 'usage', id],
    async () => {
      const result = await axios.get<BlockUsageReponse>(URLS.blocks.usage(id), APIUtils.tokenHeader(token!));

      return result.data;
    },
    { ...QueryConfig, enabled: !!token, retry: false },
  );
}

function getSearchErrorMessage(error: unknown): string | null {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const errors: unknown = error.response?.data?.errors;
  if (!Array.isArray(errors) || errors.length === 0) {
    return null;
  }

  return errors.map((entry) => (typeof entry === 'string' ? entry : (entry?.message ?? JSON.stringify(entry)))).join('\n');
}

function shouldRetryListBlocks(failureCount: number, error: unknown): boolean {
  const isClientError = axios.isAxiosError(error) && !!error.response && error.response.status < 500;
  // A server-explained failure (e.g. the 503 "Search took too long" query timeout) would fail the same way again
  const isExplainedError = getSearchErrorMessage(error) !== null;
  return !isClientError && !isExplainedError && failureCount < 3;
}

export function useListBlocks(request: BlockSearchRequest | null, disabled: boolean = false) {
  const [token] = useRecoilState(tokenState);

  return useQuery(
    [BLOCK_CACHE_KEY, 'list', request],
    async () => {
      try {
        const data = await axios.post<BlockListResponse>(URLS.blocks.search(), request, APIUtils.tokenHeader(token!));
        return data.data;
      } catch (error) {
        // Surface the server's validation messages instead of the generic "Request failed with status code 400"
        const message = getSearchErrorMessage(error);
        if (message && error instanceof Error) {
          error.message = message;
        }

        throw error;
      }
    },
    { ...QueryConfig, enabled: !!token && !disabled && !!request, keepPreviousData: true, retry: shouldRetryListBlocks },
  );
}

/** The classic block search (category dropdown and "Show archive"), GET /v2/blocks/:page/:limit */
export function useLegacyListBlocks(
  page: number,
  limit: number = 15,
  name?: string,
  manuscript?: string,
  tags?: string,
  archived?: boolean,
  description?: string,
  disabled: boolean = false,
) {
  const [token] = useRecoilState(tokenState);

  return useQuery(
    [BLOCK_CACHE_KEY, 'list', 'legacy', page, limit, name, manuscript, tags, archived, description],
    async () => {
      const data = await axios.get<BlockListResponse>(
        URLS.blocks.list(page, limit, name, manuscript, tags, archived, description),
        APIUtils.tokenHeader(token!),
      );
      return data.data;
    },
    { ...QueryConfig, enabled: !!token && !disabled, keepPreviousData: true },
  );
}
